import CurveInfo from '../CurveInfo';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { filteredGis } from '../../Tablet/Header/Gis/helpers';
import { useActiveResearches, useGis } from '../../TabletDataProvider/hooks';

export const useActiveItem = () => {
    const findItem = (data, key) => {
        for (const item of data) {
            if (item.key === key) {
                return item;
            } else if (item.children) {
                const res = findItem(item.children, key);
                if (res) {
                    return findItem(item.children, key);
                }
            }
        }
    };
    const lists = useSelector(state => state.lists);
    if (!lists.activeItemKey || !lists.data) {
        return;
    } else {
        return findItem(lists.data, lists.activeItemKey);
    }
};

export const useSelectedItem = () => {
    const item = useActiveItem();
    const calculationObject = useSelector(state => state.tree.calculationObject);
    if(item){
        return item;
    } else {
        return calculationObject;
    }
};

const CurveMeta = props => {
    const { curvesData } = props;
    const currentItem = useActiveItem();

    const researches = useActiveResearches(currentItem?.id, currentItem?.key);
    const gis = useGis(currentItem?.key, researches ?? []);

    const gisColumns = useMemo(() => filteredGis(gis), [gis]);
    const updatedData = gisColumns
        .flatMap(e => e)
        .map(curve => {
            return {
                ...curve,
                width: curve.settings.lineWidth,
                color: curve.settings.color,
                depthMin: curve.curveData.depthStart,
                depthMax: curve.curveData.depthStop,
            };
        });

    const getAllMinMax = data => {
        const curveId = data?.id;

        const minMax = [];

        if (!curveId) {
            return minMax;
        }

        const filteredCurves = Object.fromEntries(
            Object.entries(curvesData).filter(([key, _]) => key.includes(curveId)),
        );

        Object.values(filteredCurves).forEach(c => {
            if (!minMax.length) {
                minMax[0] = c.xStart;
                minMax[1] = c.xStop;
                return;
            }
            if (c.xStart < minMax[0]) {
                minMax[0] = c.xStart;
            }
            if (c.xStop > minMax[1]) {
                minMax[1] = c.xStop;
            }
        });

        return minMax;
    };

    if (isEmpty(updatedData)) return null;

    const singleCurve = data => {
        const minMax = getAllMinMax(data);

        if (!data || !minMax) {
            return null;
        }

        return (
            <div className="p-2 text-xs">
                <CurveInfo
                    width={data?.width}
                    type={data?.type}
                    color={data?.color}
                    title={data?.title}
                    depth={data?.depthMin}
                    withDate={false}
                    withDepth={false}
                />
                <div>
                    <div className="grid h-8 grid-cols-3 items-center">
                        <span className="col-start-1 col-end-2 pl-2 text-left text-xs text-gray-700 dark:text-font-secondary">
                            Дата
                        </span>
                        <span className="col-start-2 col-end-4 flex h-full items-center">
                            <span className="text-black dark:text-font-quarternary">{data?.date}</span>
                        </span>
                    </div>

                    <div className="grid h-8 grid-cols-3 items-center">
                        <span className="col-start-1 col-end-2 pl-2 text-left text-xs text-gray-700 dark:text-font-secondary">
                            Код
                        </span>
                        <span className="col-start-2 col-end-4 flex h-full items-center">
                            <span className=" text-black dark:text-font-quarternary">{data?.code}</span>
                        </span>
                    </div>

                    <div className="grid h-full grid-cols-3 items-center" style={{ minHeight: '32px' }}>
                        <span className="col-start-1 col-end-2 pl-2 text-left text-xs text-gray-700 dark:text-font-secondary">
                            Группа
                        </span>
                        <span className="col-start-2 col-end-4 flex h-full items-center">
                            <span className=" text-black dark:text-font-quarternary">{data?.group}</span>
                        </span>
                    </div>

                    <div className="grid h-8 grid-cols-3 items-center">
                        <span className="col-start-1 col-end-2 pl-2 text-left text-xs text-gray-700 dark:text-font-secondary">
                            Интервал
                        </span>
                        <span className="col-start-2 col-end-4 flex h-full items-center">
                            <span className=" text-black dark:text-font-quarternary">
                                {`${data?.depthMin.toFixed(2)}`} - {`${data?.depthMax.toFixed(2)}`}
                            </span>
                        </span>
                    </div>

                    <div className="grid h-8 grid-cols-3 items-center">
                        <span className="col-start-1 col-end-2 pl-2 text-left text-xs text-gray-700 dark:text-font-secondary">
                            Значение
                        </span>
                        <span className="col-start-2 col-end-4 flex h-full items-center">
                            <span className=" text-black dark:text-font-quarternary">
                                от {minMax[0].toFixed(3)} до {minMax[1].toFixed(3)}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const pluralCurves = data => {
        return (
            <div className="flex h-full flex-col overflow-y-auto p-2">
                {data.map((s, i) => (
                    <CurveInfo
                        key={i}
                        id={s.id}
                        width={s.width}
                        type={s.type}
                        color={s.color}
                        title={s.title}
                        date={s.date}
                        depth={s.depthMin}
                        withDate={true}
                    />
                ))}
            </div>
        );
    };

    return updatedData.length === 1 ? singleCurve(updatedData[0]) : pluralCurves(updatedData);
};

export default CurveMeta;
